import { BedDouble } from 'lucide-react'
import { formatCurrency } from '@hotel/shared/utils'
import { getPrimaryImage } from '../api/client'
import { roomImageFallback } from './roomImageFallbacks'
import type { AvailabilityResult, SearchState } from '../api/types'
import { TaxNote } from './PriceBreakdown'
import { roomCapacityText } from './roomCapacityText'

// Cinematic alternating layout. Strips amenity chips and availability pills —
// the homepage needs silence, not data density. Details live in the modal.

export function RoomsShowcase({
  rooms,
  search,
  onDetail,
  onBook,
  eyebrow,
  heading,
  lede,
  emptyState,
  errorState,
  neutralState,
}: {
  rooms: AvailabilityResult[]
  search: SearchState
  onDetail: (room: AvailabilityResult) => void
  onBook: (room: AvailabilityResult) => void
  eyebrow: string
  heading?: string
  lede?: string
  emptyState?: React.ReactNode
  errorState?: React.ReactNode
  neutralState?: React.ReactNode
}) {
  return (
    <section className="rooms-showcase" id="rooms" aria-labelledby="rooms-heading">
      <div className="rooms-showcase-inner">
        <header className="section-intro reveal-on-scroll">
          <span className="eyebrow">{eyebrow}</span>
          <h2 id="rooms-heading">{heading || 'Descansa en Comayagua'}</h2>
          {lede && <p className="rooms-lede">{lede}</p>}
        </header>

        {errorState}
        {neutralState}
        {emptyState}

        <div className="rooms-stack">
          {rooms.map((room, i) => (
            <article
              key={room.room_type_id}
              className={`room-row reveal-on-scroll ${i % 2 === 1 ? 'room-row--flip' : ''}`}
              style={{ ['--i' as string]: i } as React.CSSProperties}
              // Suppress unused `search` warning — kept in signature for
              // parity with the booking flow.
              data-nights={search.check_in}
            >
              <div className="room-row-media">
                <img
                  src={getPrimaryImage(room.images, roomImageFallback(room.name))}
                  alt={room.name}
                  loading="lazy"
                />
              </div>
              <div className="room-row-body">
                <span className="room-row-index">0{i + 1}</span>
                <h3>{room.name}</h3>
                {room.short_description && <p>{roomCapacityText(room.short_description, room.max_occupancy)}</p>}
                <div className="room-row-meta">
                  <span>
                    <BedDouble size={16} /> {room.max_occupancy} huéspedes
                  </span>
                  <span className="room-row-divider" aria-hidden>
                    ·
                  </span>
                  <span className="room-row-price">
                    <strong>{formatCurrency(room.estimated_nightly_rate)}</strong>
                    <small> / noche (promedio)</small>
                  </span>
                </div>
                <TaxNote pricing={room.pricing} />
                <div className="room-row-actions">
                  <button className="secondary-button" onClick={() => onDetail(room)}>
                    Ver detalle
                  </button>
                  <button className="primary-button" onClick={() => onBook(room)}>
                    Reservar
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
